import React from 'react';
import { useStaticQuery, graphql, Link } from 'gatsby';
import BlockContent from '@sanity/block-content-to-react';
import ImageGallery from 'react-image-gallery';
import getYoutubeID from 'get-youtube-id';
import { usePagination, useUpdateUrlFromFilters } from '../utils/hooks';
import { sortBy } from '../utils/utils';
import * as styles from './blexia.module.scss';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { RawPortableText, GatsbySanityImageFluid } from '../types';
import blexiaLogo from '../assets/images/blexia/blexiaLogo.svg';
import productsMask from '../assets/images/blexia/productsMask.svg';
import watermark from '../assets/images/blexia/watermark.svg';
import { replaceNewLinesWithBr } from '../utils/sanity';
import serializers from '../serializers';
import Video from '../components/Video';
import ProductCard from '../components/ProductCard';

type ID = string;

interface Product {
  _id: ID;
  unlisted?: boolean;
  name: string;
  slug: {
    current: string;
  };
  logoImage?: {
    asset: {
      fluid: GatsbySanityImageFluid;
    };
  };
  image?: {
    asset: {
      fluid: GatsbySanityImageFluid;
    };
  };
  type: {
    _id: ID;
    brand: 'ascenza' | 'blexia' | null;
    color: string;
    name: string;
  };
  activeSubstanceInfos?: Array<{
    activeSubstance: {
      _id: ID;
      name: string;
    };
  }>;
  problemLabel?: string;
  problemDescription?: string;
  cropDescription?: string;
}

interface QueryData {
  allSanityProduct: {
    nodes: Array<Product>;
  };
  sanityBlexia?: {
    page: {
      backgroundImage: {
        asset: {
          url: string;
        };
      };
      introSection: {
        title: string;
        featuredText: string;
        text: string;
      };
      ourProductsSection: {
        title: string;
        backgroundImage: {
          asset: {
            url: string;
          };
        };
        productsImage?: {
          asset?: {
            url: string;
          };
        };
        _rawBody: RawPortableText;
      };
      productsListSection: {
        title: string;
      };
      videos: Array<string>;
    };
  };
}

function renderVideo(item: { embedUrl: string }): React.ReactElement {
  return <Video url={item.embedUrl}></Video>;
}

const Blexia = (): React.ReactElement => {
  const data: QueryData = useStaticQuery(
    graphql`
      {
        allSanityProduct(filter: { type: { brand: { eq: "blexia" } } }) {
          nodes {
            ...Product
          }
        }
        sanityBlexia {
          page {
            backgroundImage {
              asset {
                url
              }
            }
            introSection {
              title
              featuredText
              text
            }
            ourProductsSection {
              title
              backgroundImage {
                asset {
                  url
                }
              }
              productsImage {
                asset {
                  url
                }
              }
              _rawBody(resolveReferences: { maxDepth: 4 })
            }
            productsListSection {
              title
            }
            videos
          }
        }
      }
    `,
  );
  const blexiaPage = data.sanityBlexia?.page;

  const blexiaProducts = data.allSanityProduct.nodes; //.filter(product => !product.unlisted);
  const sortedProducts = sortBy(blexiaProducts, blexiaProduct => blexiaProduct.name, 'asc');

  const {
    currentPage,
    currentPageItems: currentPageProducts,
    getPageUrlPart,
    renderPagination,
  } = usePagination(sortedProducts, 6);

  // Update url on filters state change
  useUpdateUrlFromFilters(() => {
    return [getPageUrlPart()];
  }, [currentPage]);

  return (
    <>
      <Layout className={'homepage'}>
        <SEO />
        {blexiaPage && (
          <div className={styles.content}>
            <div className={styles.heroSection}>
              <div className={styles.overlay}></div>
              <img
                className={styles.backgroundImage}
                src={blexiaPage.backgroundImage.asset.url}
                alt="/blexia"
              />
              <div className={styles.mask}></div>
              <img className={styles.logo} src={blexiaLogo} alt="" />
            </div>
            <div className={styles.contentContainer}>
              <div className={styles.introSection}>
                <h2>{blexiaPage.introSection.title}</h2>
                <div className={styles.line}></div>
                <div className={styles.featuredText}>
                  <div className={styles.backgroundBox}>
                    <p>{replaceNewLinesWithBr(blexiaPage.introSection.featuredText)}</p>
                  </div>
                </div>
                <p className={styles.introParagraph}>
                  {replaceNewLinesWithBr(blexiaPage.introSection.text)}
                </p>
              </div>
              <div className={styles.ourProductsSection}>
                <h2 className={styles.blexiaTitle}>{blexiaPage.ourProductsSection.title}</h2>
                <div className={styles.line}></div>
                <div className={styles.productsImageContainer}>
                  <img
                    className={styles.backgroundImage}
                    src={blexiaPage.ourProductsSection.backgroundImage.asset.url}
                    alt=""
                  />
                  <img className={styles.productsMask} src={productsMask} alt="" />
                  {blexiaPage.ourProductsSection.productsImage?.asset?.url && (
                    <img
                      className={styles.productImage}
                      src={blexiaPage.ourProductsSection.productsImage.asset.url}
                      alt=""
                    />
                  )}
                </div>
                <BlockContent
                  className={styles.bodyText}
                  blocks={blexiaPage.ourProductsSection._rawBody}
                  serializers={serializers}
                />
              </div>
              <div className={styles.productsListSection}>
                <h2>{blexiaPage.productsListSection.title}</h2>

                <div className={styles.blexiaProductsContainer}>
                  {currentPageProducts.map(blexiaProduct => (
                    <Link
                      key={blexiaProduct._id}
                      className={styles.cardLinkWrapper}
                      to={`/products/${blexiaProduct.slug.current}`}
                    >
                      <ProductCard product={blexiaProduct}></ProductCard>
                    </Link>
                  ))}
                  {Array.from({ length: (2 - (currentPageProducts.length % 2)) % 2 }).map(
                    (__, i: number) => (
                      <div key={i} className={styles.mockEvent}></div>
                    ),
                  )}
                </div>
                {renderPagination()}
              </div>
              <ImageGallery
                items={blexiaPage.videos.map(videoUrl => ({
                  thumbnail: 'https://i.ytimg.com/vi/' + getYoutubeID(videoUrl) + '/hqdefault.jpg',
                  embedUrl: videoUrl,
                  renderItem: renderVideo,
                }))}
                additionalClass={styles.videoGallery}
                showBullets
                showPlayButton={false}
              />
            </div>
            <div className={styles.watermark}>
              <img src={watermark} alt="" />
            </div>
          </div>
        )}
      </Layout>
    </>
  );
};

export default Blexia;
